import clsx from "clsx";
import React, { FC, ReactNode } from "react";
import { Link, LinkProps } from "react-router";

import { LocalLoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner.tsx";

type ButtonType = "submit" | "button" | "reset";
type ButtonVariant = "primary" | "secondary" | "tertiary";
type ButtonSize = "sm" | "lg";

// "eSIM" is a fixed expression with a lowercase "e" - the automatic
// first-letter capitalization below must not turn it into "ESIM". The
// trailing \b keeps this from also matching an unrelated word that merely
// starts with the same four letters (e.g. a hypothetical "Esimulate").
const FIXED_CASING_LABEL_PATTERN = /^esim\b/i;

type ColorState = "default" | "inverted";

// Every class is spelled out in full (no template strings) so Tailwind's
// source scan finds it. `native*` apply through the `disabled:` variants, so
// a natively disabled button (e.g. while loading) gets the disabled colours
// without the prop.
const variantClassMapping: Record<
  ButtonVariant,
  Record<
    ColorState,
    {
      text: string;
      bg: string;
      /** Fill while hovered/pressed - only tertiary has none at rest. */
      interactionBg?: string;
      hoverBg?: string;
      disabledText: string;
      disabledBg: string;
      nativeDisabledText: string;
      nativeDisabledBg: string;
    }
  >
> = {
  primary: {
    default: {
      text: "text-foreground-primary-inverse",
      bg: "bg-surface-primary",
      disabledText: "text-foreground-tertiary",
      disabledBg: "bg-surface-tertiary",
      nativeDisabledText: "disabled:text-foreground-tertiary",
      nativeDisabledBg: "group-disabled/btn:bg-surface-tertiary",
    },
    inverted: {
      text: "text-foreground-primary",
      bg: "bg-surface-primary-inverse",
      disabledText: "text-foreground-tertiary-inverse",
      disabledBg: "bg-surface-tertiary-inverse",
      nativeDisabledText: "disabled:text-foreground-tertiary-inverse",
      nativeDisabledBg: "group-disabled/btn:bg-surface-tertiary-inverse",
    },
  },
  secondary: {
    default: {
      text: "text-foreground-primary",
      bg: "bg-surface-secondary",
      disabledText: "text-foreground-tertiary",
      disabledBg: "bg-surface-tertiary",
      nativeDisabledText: "disabled:text-foreground-tertiary",
      nativeDisabledBg: "group-disabled/btn:bg-surface-tertiary",
    },
    inverted: {
      text: "text-foreground-primary-inverse",
      bg: "bg-surface-secondary-inverse",
      disabledText: "text-foreground-tertiary-inverse",
      disabledBg: "bg-surface-tertiary-inverse",
      nativeDisabledText: "disabled:text-foreground-tertiary-inverse",
      nativeDisabledBg: "group-disabled/btn:bg-surface-tertiary-inverse",
    },
  },
  // No fill at rest - the layer only fades in on hover/press.
  tertiary: {
    default: {
      text: "text-foreground-primary",
      bg: "bg-transparent",
      interactionBg: "bg-surface-secondary",
      hoverBg:
        "group-hover/btn:bg-surface-secondary group-active/btn:bg-surface-secondary",
      disabledText: "text-foreground-tertiary",
      disabledBg: "bg-transparent",
      nativeDisabledText: "disabled:text-foreground-tertiary",
      nativeDisabledBg: "group-disabled/btn:bg-transparent",
    },
    inverted: {
      text: "text-foreground-primary-inverse",
      bg: "bg-transparent",
      interactionBg: "bg-surface-secondary-inverse",
      hoverBg:
        "group-hover/btn:bg-surface-secondary-inverse group-active/btn:bg-surface-secondary-inverse",
      disabledText: "text-foreground-tertiary-inverse",
      disabledBg: "bg-transparent",
      nativeDisabledText: "disabled:text-foreground-tertiary-inverse",
      nativeDisabledBg: "group-disabled/btn:bg-transparent",
    },
  },
};

// The background layer shrinks by the hover inset on hover and by the
// pressed inset on press (or while `pressed`).
const sizeClassMapping: Record<
  ButtonSize,
  {
    control: string;
    iconOnly: string;
    label: string;
    icon: string;
    hoverInset: string;
    pressedInset: string;
    iconOnlyHoverInset: string;
    iconOnlyPressedInset: string;
  }
> = {
  lg: {
    control: "h-10 px-3 py-2",
    iconOnly: "h-10 w-10 p-2",
    // pt/pb: optical baseline centering for Gellix
    label: "text-body-base leading-none pt-0.5 pb-1.5",
    icon: "size-6",
    hoverInset: "group-hover/btn:inset-0.5 group-active/btn:inset-1",
    pressedInset: "inset-1",
    iconOnlyHoverInset: "group-hover/btn:inset-0.5 group-active/btn:inset-1",
    iconOnlyPressedInset: "inset-1",
  },
  sm: {
    control: "h-8 p-2",
    iconOnly: "h-8 w-8 p-2",
    label: "text-body-xs leading-none pt-0.25 pb-0.75",
    icon: "size-4",
    hoverInset: "group-hover/btn:inset-0.5 group-active/btn:inset-1",
    pressedInset: "inset-1",
    iconOnlyHoverInset: "group-hover/btn:inset-0.25 group-active/btn:inset-0.5",
    iconOnlyPressedInset: "inset-0.5",
  },
};

type BtnStyleOptions = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  inverted?: boolean;
  disabled?: boolean;
  pressed?: boolean;
  iconOnly?: boolean;
};

/**
 * Class names of the control itself. `btn` stays as a marker: the legacy
 * reset in src/styles/form.scss (`button:not(.btn)`) must skip this button.
 */
export const getBtnClassName = ({
  className,
  variant = "primary",
  size = "lg",
  inverted,
  fullWidth,
  disabled,
  iconOnly,
}: BtnStyleOptions & { className?: string; fullWidth?: boolean }) => {
  const colors =
    variantClassMapping[variant][inverted ? "inverted" : "default"];
  const sizes = sizeClassMapping[size];

  return clsx(
    // Named group: a plain `group-hover:` would also react to any outer
    // `.group` (e.g. the billing-account rows), not only to this button.
    "btn group/btn relative inline-flex cursor-pointer items-center justify-center gap-1 whitespace-nowrap border-none bg-transparent font-primary font-semibold no-underline",
    "disabled:pointer-events-none disabled:cursor-not-allowed",
    colors.nativeDisabledText,
    iconOnly ? sizes.iconOnly : sizes.control,
    // Focus ring: 2px border with a 2px gap outside the control. Kept when
    // disabled: a disabled button with a tooltip reason stays focusable.
    // `after:content-['']` or the ::after ring never paints (as NavRow).
    "outline-none after:pointer-events-none after:absolute after:-inset-1 after:rounded-lg after:border-2 after:border-solid after:border-transparent after:content-['']",
    inverted
      ? "focus-visible:after:border-foreground-primary-inverse keyboard-focus:after:border-foreground-primary-inverse"
      : "focus-visible:after:border-surface-primary keyboard-focus:after:border-surface-primary",
    disabled
      ? ["pointer-events-none cursor-not-allowed", colors.disabledText]
      : colors.text,
    fullWidth && "w-full",
    className,
  );
};

const getBtnBgClassName = ({
  variant = "primary",
  size = "lg",
  inverted,
  disabled,
  pressed,
  iconOnly,
}: BtnStyleOptions) => {
  const colors =
    variantClassMapping[variant][inverted ? "inverted" : "default"];
  const sizes = sizeClassMapping[size];

  if (disabled) {
    return clsx("absolute inset-0 z-0 rounded-md", colors.disabledBg);
  }

  return clsx(
    "absolute z-0 rounded-md duration-120 ease-standard",
    colors.interactionBg ? "transition-inset-colors" : "transition-inset",
    colors.nativeDisabledBg,
    pressed
      ? [
          iconOnly ? sizes.iconOnlyPressedInset : sizes.pressedInset,
          colors.interactionBg ?? colors.bg,
        ]
      : [
          "inset-0",
          colors.bg,
          iconOnly ? sizes.iconOnlyHoverInset : sizes.hoverInset,
          colors.hoverBg,
        ],
  );
};

const getBtnLabelClassName = (children: ReactNode, size: ButtonSize) =>
  clsx(
    "relative inline-block",
    sizeClassMapping[size].label,
    // Labels come from translations - capitalize only the first letter so
    // casing stays consistent regardless of the source string. The fixed
    // casing case still gets a ::first-letter rule (normal-case) so the
    // label renders exactly like every other one.
    typeof children === "string" && FIXED_CASING_LABEL_PATTERN.test(children)
      ? "first-letter:normal-case"
      : "first-letter:uppercase",
  );

/**
 * The button's inside (background layer, label, icon). Exported for a control
 * that has to own its <button> itself, e.g. a Headless UI Disclosure.Button;
 * pair it with getBtnClassName and the same style options.
 */
export const ButtonContent = ({
  children,
  icon,
  loading,
  ...styleOptions
}: BtnStyleOptions & {
  children?: ReactNode;
  icon?: ReactNode;
  loading?: boolean;
}) => {
  const size = styleOptions.size ?? "lg";

  return (
    <>
      <span className={getBtnBgClassName(styleOptions)} aria-hidden="true" />
      {loading && (
        <LocalLoadingSpinner
          className="relative z-10 !mr-2 !size-4"
          decorative
        />
      )}
      {children && (
        <span className={getBtnLabelClassName(children, size)}>{children}</span>
      )}
      {icon && (
        <span
          className={clsx(
            "relative flex items-center justify-center *:block *:size-full",
            sizeClassMapping[size].icon,
          )}
        >
          {icon}
        </span>
      )}
    </>
  );
};

/**
 * A control without visible text has to name itself, so `ariaLabel` is only
 * optional as long as there are children to read out. Without children it is
 * required, which makes an unnamed icon-only button a compile error.
 */
type LabelledProps =
  | { children: ReactNode; ariaLabel?: string }
  | { children?: never; ariaLabel: string };

type ButtonProps = LabelledProps & {
  dataTestid?: string;
  className?: string;
  disabled?: boolean;
  type?: ButtonType;
  /** Submits/resets a `<form>` this button isn't nested inside, by id. */
  form?: string;
  fullWidth?: boolean;
  loading?: boolean;
  variant?: ButtonVariant;
  size?: ButtonSize;
  inverted?: boolean;
  icon?: ReactNode;
  pressed?: boolean;
  onClick?: (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => void | Promise<void>;
  /**
   * For a control that toggles an input it must not steal focus from - the
   * handler calls `preventDefault()` so the input keeps focus on mousedown.
   */
  onMouseDown?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Set by HintTooltip so the reason is announced on focus. */
  "aria-describedby"?: string;
  /** For a button that toggles a disclosure - e.g. the "show code" toggle. */
  "aria-expanded"?: boolean;
  "aria-controls"?: string;
};

export const Button: FC<ButtonProps> = ({
  dataTestid,
  className,
  children,
  disabled,
  // A `<button>` with no explicit type defaults to "submit" inside a <form> -
  // every caller that actually wants that (Save/Confirm) already passes
  // type="submit" itself, so every other button must default to "button" or
  // it silently submits (and resets) the surrounding form on click.
  type = "button",
  form,
  fullWidth,
  loading,
  variant = "primary",
  size = "lg",
  inverted,
  icon,
  pressed,
  ariaLabel,
  "aria-describedby": ariaDescribedBy,
  "aria-expanded": ariaExpanded,
  "aria-controls": ariaControls,
  onClick = () => null,
  onMouseDown,
}) => {
  const iconOnly = !children && !!icon;
  const styleOptions = { variant, size, inverted, disabled, pressed, iconOnly };

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || loading) {
      // Disabled-with-a-tooltip-reason keeps the native `disabled` attribute
      // off (see nativeDisabled below), so without this the browser would
      // still run a type="submit"/"reset" button's default form action.
      event.preventDefault();

      return;
    }

    try {
      await onClick(event);
    } catch (error) {
      console.error("Button onClick error:", error);
    }
  };

  // A tooltip reason (HintTooltip sets aria-describedby) has to stay
  // reachable by keyboard, which the native `disabled` attribute prevents
  // outright - same non-native-disabled trick ButtonLink already uses.
  // Loading is transient and carries no reason to announce, so it keeps
  // the native attribute.
  const nativeDisabled = loading || (disabled && !ariaDescribedBy);

  return (
    <div className={clsx(fullWidth ? "flex w-full" : "inline-flex")}>
      <button
        data-testid={dataTestid}
        className={getBtnClassName({ ...styleOptions, fullWidth, className })}
        type={type}
        form={form}
        // Explicit 0: Safari skips plain buttons in the Tab cycle otherwise.
        tabIndex={nativeDisabled ? -1 : 0}
        onClick={handleClick}
        // Same guard as handleClick: a non-natively-disabled button (one with
        // a tooltip reason) still dispatches mousedown, and a disabled control
        // must not run its consumer's handler.
        onMouseDown={(event) => {
          if (disabled || loading) {
            return;
          }

          onMouseDown?.(event);
        }}
        aria-busy={loading}
        aria-disabled={disabled || loading}
        aria-pressed={pressed}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-expanded={ariaExpanded}
        aria-controls={ariaControls}
        disabled={nativeDisabled}
      >
        <ButtonContent {...styleOptions} icon={icon} loading={loading}>
          {children}
        </ButtonContent>
      </button>
    </div>
  );
};

type LinkButtonProps = LabelledProps & {
  className?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  variant?: ButtonVariant;
  size?: ButtonSize;
  inverted?: boolean;
  icon?: ReactNode;
  /** Set by HintTooltip so the reason is announced on focus. */
  "aria-describedby"?: string;
  "data-testid"?: string;
};

/**
 * Shared by ButtonLink and ButtonAnchor: a disabled link renders as a span,
 * since an anchor cannot be disabled.
 */
const LinkButtonShell = ({
  className,
  children,
  disabled,
  fullWidth,
  variant = "primary",
  size = "lg",
  inverted,
  icon,
  ariaLabel,
  "aria-describedby": ariaDescribedBy,
  "data-testid": dataTestid,
  renderLink,
}: LinkButtonProps & {
  renderLink: (linkProps: {
    className: string;
    content: ReactNode;
  }) => ReactNode;
}) => {
  const iconOnly = !children && !!icon;
  const styleOptions = { variant, size, inverted, disabled, iconOnly };
  const btnClassName = getBtnClassName({
    ...styleOptions,
    fullWidth,
    className,
  });
  const content = (
    <ButtonContent {...styleOptions} icon={icon}>
      {children}
    </ButtonContent>
  );

  return (
    <div className={clsx(fullWidth ? "flex w-full" : "inline-flex")}>
      {disabled ? (
        <span
          data-testid={dataTestid}
          className={btnClassName}
          // Focusable so keyboard users reach the disabled control and hear
          // the tooltip that explains why it is unavailable.
          tabIndex={ariaDescribedBy ? 0 : undefined}
          // Still a link, just unavailable - as in TabWithDisableHint.
          role={ariaDescribedBy ? "link" : undefined}
          aria-disabled="true"
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
        >
          {content}
        </span>
      ) : (
        renderLink({ className: btnClassName, content })
      )}
    </div>
  );
};

export const ButtonLink: FC<
  Omit<LinkProps, "children"> &
    React.RefAttributes<HTMLAnchorElement> &
    LinkButtonProps
> = ({
  className,
  children,
  disabled,
  fullWidth,
  variant,
  size,
  inverted,
  icon,
  ariaLabel,
  "aria-describedby": ariaDescribedBy,
  "data-testid": dataTestid,
  ...props
}) => (
  <LinkButtonShell
    {...({ children, ariaLabel } as LabelledProps)}
    data-testid={dataTestid}
    className={className}
    disabled={disabled}
    fullWidth={fullWidth}
    variant={variant}
    size={size}
    inverted={inverted}
    icon={icon}
    aria-describedby={ariaDescribedBy}
    renderLink={({ className: linkClassName, content }) => (
      <Link
        data-testid={dataTestid}
        className={linkClassName}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        // Links are natively draggable: pressing, dragging off and releasing
        // elsewhere starts the browser's drag loop, which freezes the page.
        draggable={false}
        {...props}
      >
        {content}
      </Link>
    )}
  />
);

/** Design-system button as a plain anchor, for external links. */
export const ButtonAnchor: FC<
  LinkButtonProps & {
    href?: string;
    /** Defaults to a new tab, the usual case for an external link. */
    target?: "_self" | "_blank";
  }
> = ({ href, target = "_blank", ariaLabel, children, ...props }) => (
  <LinkButtonShell
    {...({ children, ariaLabel } as LabelledProps)}
    {...props}
    renderLink={({ className, content }) => (
      <a
        data-testid={props["data-testid"]}
        className={className}
        aria-label={ariaLabel}
        aria-describedby={props["aria-describedby"]}
        href={href}
        target={target}
        rel="noopener noreferrer"
        // See ButtonLink: prevent the native link drag loop.
        draggable={false}
      >
        {content}
      </a>
    )}
  />
);

export const SubmitButton = ({
  dataTestid,
  className,
  label,
  disabled,
  fullWidth = true,
  size = "lg",
}: {
  dataTestid?: string;
  className?: string;
  label: ReactNode;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: ButtonSize;
}) => (
  <Button
    dataTestid={dataTestid}
    className={className}
    variant="primary"
    type="submit"
    disabled={disabled}
    fullWidth={fullWidth}
    size={size}
  >
    {label}
  </Button>
);
